import { ExcalidrawImageElement, FileId } from "@excalidraw/excalidraw/types/element/types"
import { DataURL } from "@excalidraw/excalidraw/types/types"
import { Section } from "shared/lessons"
import { DrawingAnswer } from "shared/types/studentTypes"

const MAX_CANVAS_WIDTH = window.innerWidth * 0.5
const MAX_CANVAS_HEIGHT = window.innerHeight * 0.5

export const convertBlobToBase64 = async (blob: Blob): Promise<string> => {
    if (!blob) return null
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = async function () {
          if (typeof reader.result === "string") {
            resolve(reader.result)
          } else {
            const dec = new TextDecoder()
            resolve(dec.decode(reader.result))
          }
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

export const getImgDimensions = (base64String: string) : Promise<{ width: number; height: number }> => {
  if (!base64String) return

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = base64String
    img.onload = () => {
      const scaledWidth = img.width * Math.min(1, MAX_CANVAS_WIDTH / img.width)
      const scaledHeight = img.height * Math.min(1, MAX_CANVAS_HEIGHT / img.height)

      resolve({ width: Math.min(img.width, scaledWidth), height: Math.min(img.height, scaledHeight) })
    }
    img.onerror = async () => {
      reject({ width: 700, height: 400 })
    }
  })
}

export const loadImage = async ({ initialDrawingData, imagePath, fileId, imageId }: { initialDrawingData?: DrawingAnswer, imagePath: string, fileId: string, imageId: string }) => {
  let backgroundResponse = null
  let drawingDataResponse = null
  let responseUrl: string, savedState: string

  responseUrl = initialDrawingData?.drawingResponse
  savedState = initialDrawingData?.answer

  if (!savedState && responseUrl) {
    drawingDataResponse = await fetch(responseUrl)
  }

  if (imagePath) {
    backgroundResponse = await fetch(imagePath)
  }

  let backgroundBlob: Blob = null
  let backgroundType = null
  let drawingDataBlob: Blob = null
  let drawingDataType = null

  if (backgroundResponse?.ok) {
    backgroundBlob = await backgroundResponse.blob()
    backgroundType = backgroundBlob.type
  }

  if (drawingDataResponse?.ok) {
    drawingDataBlob = await drawingDataResponse.blob()
    drawingDataType = drawingDataBlob.type
  }

  /**
   * Get base 64 string for background image and its dimensions
   */
  let backgroundBase64: string
  let backgroundDimensions: {width: number, height: number} = null

  try {
    backgroundBase64 = await convertBlobToBase64(backgroundBlob)
  } catch {
    backgroundBase64 = null
  }

  try {
    backgroundDimensions = await getImgDimensions(backgroundBase64)
  } catch (defaultDimensions) {
    backgroundDimensions = defaultDimensions
  }

  /**
   * Get base 64 string for previous drawing image and its dimensions
   */

  let drawingDataBase64: string
  let drawingDataDimensions: {width: number, height: number} = null

  try {
    drawingDataBase64 = await convertBlobToBase64(drawingDataBlob)
  } catch {
    drawingDataBase64 = null
  }

  try {
    drawingDataDimensions = await getImgDimensions(drawingDataBase64)
  } catch (defaultDimensions) {
    drawingDataDimensions = defaultDimensions
  }

  let elements = []
  let state: Record<string, unknown>
  const files = {}

  if (savedState) {
    const savedDrawingData: { elements: unknown[], state: Record<string, unknown> } = JSON.parse(savedState)
    elements = savedDrawingData.elements
    state = savedDrawingData.state
    if (state) {
      state.collaborators = new Map(Object.entries(savedDrawingData.state.collaborators ?? {}))
    }
  }

  /* If there is previous drawings, place background image in a position where it fits with other drawings */
  let backgroundImagePosition = { x: 800, y: 800 }
  if (savedState) {
      const total = elements.reduce((acc, el) => {
        acc.x += el.x
        acc.y += el.y
        return acc
      }, { x: 0, y: 0 })
      backgroundImagePosition =  { x: total.x / elements.length, y: total.y / elements.length }
  }

  if (backgroundBase64 && backgroundDimensions) {
    let backgroundElement: ExcalidrawImageElement = elements.find((element) => element.fileId === `background-${fileId}`)
    if (!backgroundElement) {
      backgroundElement = {
        type: "image",
        id: `background-${imageId}`,
        status: "saved",
        fileId: `background-${fileId}` as FileId,
        version: 2,
        versionNonce: Date.now(),
        x: backgroundImagePosition.x,
        y: backgroundImagePosition.y,
        width: backgroundDimensions.width,
        height: backgroundDimensions.height,
        scale: [1, 1],
        isDeleted: false,
        fillStyle: "hachure",
        strokeWidth: 1,
        strokeStyle: "solid",
        roughness: 1,
        opacity: 100,
        groupIds: [],
        strokeColor: "#000000",
        backgroundColor: "transparent",
        seed: Date.now(),
        roundness: null,
        angle: 0,
        frameId: null,
        boundElements: null,
        updated: 1,
        locked: true,
        link: null,
      }
      elements.unshift(backgroundElement)
    }
    files[backgroundElement.fileId] = {
      mimeType: backgroundType,
      id: backgroundElement.fileId,
      dataURL: backgroundBase64 as DataURL,
      created: Date.now(),
    }
  }

  if (drawingDataBase64 && drawingDataDimensions) {
    const drawingElement: ExcalidrawImageElement = {
      type: "image",
      id: `drawing-${imageId}`,
      status: "saved",
      fileId: `drawing-${fileId}` as FileId,
      version: 2,
      versionNonce: Date.now(),
      x: 800,
      y: 800,
      width: drawingDataDimensions.width,
      height: drawingDataDimensions.height,
      scale: [1, 1],
      isDeleted: false,
      fillStyle: "hachure",
      strokeWidth: 1,
      strokeStyle: "solid",
      roughness: 1,
      opacity: 100,
      groupIds: [],
      strokeColor: "#000000",
      backgroundColor: "transparent",
      seed: Date.now(),
      roundness: null,
      angle: 0,
      frameId: null,
      boundElements: null,
      updated: 1,
      locked: false,
      link: null,
    }
    elements.push(drawingElement)
    files[drawingElement.fileId] = {
      mimeType: drawingDataType,
      id: drawingElement.fileId,
      dataURL: drawingDataBase64 as DataURL,
      created: Date.now(),
    }
  }

  return {
    elements: elements,
    scrollToContent: !savedState,
    files: files,
    appState: state,
  }
}

/**
 * 
 * Creates an object with keys for each drawing lesson question in the format 'question-:sectionNumber-:questionNumber' and assign null to each key
 */
export const initLessonDrawingResponses = async (sections: Section[]) => {
  const data = {}
  sections.forEach((section, sectNumber) => {
    section?.content.forEach(task => {
      if (task.category === "Question" && task.isDrawing === "Yes") {
        data[`question-${sectNumber + 1}-${task.index}`] = null
      }
    })
  })
  return data
}

/**
 * 
 * Load the initial lesson drawing data from previous attempt
 */
export const loadLessonDrawingData = async (initialDrawingData: Record<string, DrawingAnswer>, data: any): Promise<Record<string, DrawingAnswer>> => {
  if (!initialDrawingData) return data
  const res: Record<string, DrawingAnswer> = {}
  const promises = Object.keys(data).map(async key => {
    if (!initialDrawingData[key]?.drawingResponse) return res[key] = null
    const response = await fetch(initialDrawingData[key].drawingResponse)
    if (!response.ok) return
    const blob = await response.blob()
    const base64String = await convertBlobToBase64(blob)
    res[key] = {
      drawingResponse: base64String,
      answer: initialDrawingData[key].answer,
    }
  })
  await Promise.all(promises)
  return res
}

/**
 * 
 * Convert an AWS S3 drawing image url to a base64 string 
 */
export const convertImgToBase64 = async (url: string) => {
  const response = await fetch(url)
  if (!response.ok) return
  const blob = await response.blob()
  const base64String = await convertBlobToBase64(blob)
  return base64String
}
